import { useEffect, useRef, useState } from 'react';
import { FileAudio, FileText, Link2, Mic, Paperclip, Plus, Trash2 } from 'lucide-react';
import { useT } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../context/RoleContext';
import { ConfirmModal } from '../../components/ConfirmModal';
import { AudioImportDialog } from '../../components/AudioImportDialog';
import { TranscriptModal } from '../../components/TranscriptModal';
import { RedoTranscriptButton } from '../../components/RedoTranscript';
import { useLiveChanges } from '../../live/live';
import { isAudioDoc, isAudioFile } from '../../utils/audio';
import { docApi, docHref, type DocItem, type DocTarget } from './api';

// [Proposition: documents] The documents of one to-do item, task or milestone:
// each with who added it and when, opened in a new tab (a link goes straight
// to the external drive). Files are attached from the computer; links point
// to a document kept on SharePoint, OneDrive, Google Drive… Only the person
// who added a document, or the PMO, can delete it. Additions and deletions are
// logged in the project's history.

// Static keys so the i18n checker can resolve them.
const HEADER_KEY: Record<DocTarget['type'], string> = {
  todo: 'docs.titleTodo', task: 'docs.titleTask', milestone: 'docs.titleMilestone', project: 'docs.titleProject',
};
const HINT_KEY: Partial<Record<DocTarget['type'], string>> = {
  task: 'docs.hintTask', milestone: 'docs.hintMilestone',
};

export function fmtDocDate(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Speakr is still at it (statuses as Speakr names them, plus PMO's own UPLOADING). */
export const transcriptPending = (status: string | null | undefined) =>
  /^(uploading|pending|processing|summarizing|stitching|queued)$/i.test(status || '');

/**
 * The transcript of an audio document, beside its name: "Transcription en
 * cours…" while Speakr works, then a button opening it.
 */
export function DocTranscript({ doc }: { doc: { name: string; mime_type?: string | null; transcription_id: number | null; transcription_status: string | null } }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  if (!doc.transcription_id || !isAudioDoc(doc)) return null;
  if (transcriptPending(doc.transcription_status)) {
    return <span className="docs-transcript docs-transcript--pending"><Mic size={11} /> {t('timeline.transcribing')}</span>;
  }
  if (/^failed$/i.test(doc.transcription_status || '')) {
    return (
      <span className="docs-transcript-failed">
        <span className="docs-transcript docs-transcript--failed">{t('timeline.transcriptFailed')}</span>
        <RedoTranscriptButton transcriptionId={doc.transcription_id} className="docs-transcript" />
      </span>
    );
  }
  return (
    <>
      <button type="button" className="docs-transcript" onClick={() => setOpen(true)}>
        <FileText size={11} /> {t('docs.transcript')}
      </button>
      {open && <TranscriptModal transcriptionId={doc.transcription_id} title={doc.name} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * "Joindre un fichier" and "Ajouter un lien" for one target. An audio file
 * first asks whether it is a meeting or an audio document (AudioImportDialog).
 * Used under a list of documents, and in the project page's side panel.
 */
export function DocumentsAdd({ target, confirmTarget, disabled = false, onAdded }: {
  target: DocTarget;
  /** When set, each addition asks for confirmation naming this target. */
  confirmTarget?: string;
  disabled?: boolean;
  /** After each addition (to reload the list it sits under). */
  onAdded: () => unknown;
}) {
  const { t } = useT();
  const [linkOpen, setLinkOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{ label: string; run: () => void } | null>(null);
  // Audio files picked: each asks, in turn, whether it is a meeting or an audio document.
  const [audioQueue, setAudioQueue] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const after = (p: Promise<unknown>) => {
    setBusy(true);
    return p.then(() => onAdded()).catch((e: Error & { code?: string }) =>
      alert(e.code === 'BAD_URL' ? t('docs.badUrl') : e.message)).finally(() => setBusy(false));
  };

  const confirmThen = (label: string, run: () => void) => {
    if (confirmTarget) setPendingAdd({ label, run });
    else run();
  };

  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const all = Array.from(files);
    if (fileRef.current) fileRef.current.value = '';
    const audio = all.filter(isAudioFile);
    const list = all.filter(f => !isAudioFile(f));
    if (audio.length > 0) setAudioQueue(q => [...q, ...audio]);
    if (list.length > 0) confirmThen(list.map(f => f.name).join(', '), () => after(docApi.upload(target, list)));
  };
  const nextAudio = () => {
    setAudioQueue(q => q.slice(1));
    Promise.resolve(onAdded()).catch(() => {});
  };
  const addLink = () => {
    if (!url.trim()) return;
    const u = url.trim(), n = name.trim();
    confirmThen(n || u, () =>
      after(docApi.addLink(target, u, n).then(() => { setUrl(''); setName(''); setLinkOpen(false); })));
  };
  const off = busy || disabled;

  return (
    <>
      <div className="docs-actions">
        <input ref={fileRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} />
        <button type="button" className="btn btn-ghost btn-sm" disabled={off} onClick={() => fileRef.current?.click()}>
          <Plus size={12} /> {t('docs.addFile')}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={off} onClick={() => setLinkOpen(v => !v)}>
          <Link2 size={12} /> {t('docs.addLink')}
        </button>
      </div>
      {linkOpen && (
        <div className="docs-link-form">
          <input className="form-input" placeholder={t('docs.linkUrlPlaceholder')} value={url} autoFocus
            onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addLink(); }} />
          <input className="form-input" placeholder={t('docs.linkNamePlaceholder')} value={name}
            onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addLink(); }} />
          <button type="button" className="btn btn-accent btn-sm" disabled={off || !url.trim()} onClick={addLink}>{t('docs.linkSave')}</button>
        </div>
      )}
      {audioQueue.length > 0 && (
        <AudioImportDialog
          key={`${audioQueue[0].name}-${audioQueue[0].lastModified}-${audioQueue.length}`}
          file={audioQueue[0]}
          target={target}
          documentsLabel={confirmTarget}
          attachPlain={(f) => docApi.upload(target, [f])}
          onCancel={() => setAudioQueue(q => q.slice(1))}
          onDone={nextAudio}
        />
      )}
      <ConfirmModal
        open={pendingAdd != null}
        title={t('docs.addConfirmTitle')}
        message={pendingAdd ? t('docs.addConfirm', { name: pendingAdd.label, target: confirmTarget ?? '' }) : ''}
        confirmLabel={t('docs.addConfirmAction')}
        onConfirm={() => { const p = pendingAdd; setPendingAdd(null); p?.run(); }}
        onCancel={() => setPendingAdd(null)}
      />
    </>
  );
}

/** What the delete prompt needs to say about a document. */
export interface DocToDelete {
  id: number;
  name: string;
  kind: 'file' | 'link';
  inComment: boolean;
  isRecording: boolean;
  /** What it is attached to, when the list doesn't already show it. */
  where?: string;
}

/**
 * Deleting a document from any list: `ask(doc)` opens the prompt (render
 * `prompt` once in the list), which says what will go. `deleting` is the
 * document on its way out, to turn its button off. One already gone (deleted
 * by someone else, or in another tab) counts as deleted, so the list catches
 * up instead of keeping a row nothing can be done with.
 */
export function useDeleteDocument(onDeleted: () => unknown) {
  const { t } = useT();
  const [pending, setPending] = useState<DocToDelete | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  const confirm = () => {
    const d = pending;
    setPending(null);
    if (!d) return;
    setDeleting(d.id);
    docApi.remove(d.id)
      .catch((e: Error & { status?: number }) => { if (e.status !== 404) throw e; })
      .then(onDeleted)
      .catch((e: Error) => alert(e.message))
      .finally(() => setDeleting(null));
  };

  const effect = !pending ? ''
    : pending.kind === 'link' ? t('docs.removeLink')
    : pending.isRecording ? t('docs.removeRecording')
    : pending.inComment ? t('docs.removeFromComment')
    : t('docs.removeFile');
  const message = pending
    ? [t('docs.removeAsk', { name: pending.name }), pending.where ? t('docs.removeWhere', { where: pending.where }) : '', effect].filter(Boolean).join(' ')
    : '';

  const prompt = (
    <ConfirmModal
      open={pending != null}
      title={t('docs.remove')}
      message={message}
      confirmLabel={t('docs.remove')}
      destructive
      onConfirm={confirm}
      onCancel={() => setPending(null)}
    />
  );
  return { ask: setPending, deleting, prompt };
}

export function DocumentsList({ target, readOnly = false, compact = false, showHeader = true, confirmTarget, onChanged, reloadKey }: {
  target: DocTarget;
  readOnly?: boolean;
  /** Tighter layout, for a to-do item. */
  compact?: boolean;
  showHeader?: boolean;
  /** When set (e.g. "Jalon 2 · Livraison"), each addition asks for confirmation
   *  naming this target, so a slip doesn't attach a document to the wrong milestone. */
  confirmTarget?: string;
  onChanged?: (count: number) => void;
  /** Changes when documents were changed elsewhere on the page. */
  reloadKey?: unknown;
}) {
  const { t } = useT();
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [docs, setDocs] = useState<DocItem[] | null>(null);
  const targetKey = `${target.type}:${target.id}`;

  const load = () => docApi.list(target).then(d => { setDocs(d); return d; });
  useEffect(() => { setDocs(null); load().catch(() => setDocs([])); }, [targetKey]);
  const seenReloadKey = useRef(reloadKey);
  useEffect(() => {
    if (seenReloadKey.current === reloadKey) return;
    seenReloadKey.current = reloadKey;
    load().catch(() => {});
  }, [reloadKey]);
  // A transcript under way finishes in the background: pick it up when the server says so.
  useLiveChanges(() => {
    if (docs?.some(d => d.transcription_id && transcriptPending(d.transcription_status))) load().catch(() => {});
  });

  const removal = useDeleteDocument(() => load().then(d => onChanged?.(d.length)));

  return (
    <div className={`docs${compact ? ' docs--compact' : ''}`}>
      {showHeader && (
        <>
          {/* Says what the documents are attached to, so a task's documents aren't mistaken for an item's. */}
          <div className="docs-header">
            <Paperclip size={13} /> {t(HEADER_KEY[target.type])}{docs && docs.length > 0 ? ` (${docs.length})` : ''}
          </div>
          {HINT_KEY[target.type] && <div className="docs-hint">{t(HINT_KEY[target.type]!)}</div>}
        </>
      )}
      {compact && target.type === 'todo' && <div className="docs-hint">{t('docs.hintTodo')}</div>}
      {docs && docs.length === 0 && !compact && <div className="vjt-muted">{t('docs.empty')}</div>}
      {docs?.map(d => (
        <div key={d.id} className="docs-item">
          {d.kind === 'link' ? <Link2 size={13} /> : isAudioDoc(d) ? <FileAudio size={13} /> : <FileText size={13} />}
          <div className="docs-item-main">
            <a href={docHref(d)} target="_blank" rel="noopener noreferrer" className="docs-name">{d.name}</a>
            <span className="docs-meta">
              {d.kind === 'link' ? t('docs.kindLink') : t('docs.kindFile')} · {t('docs.addedBy', { who: d.added_by, date: fmtDocDate(d.added_at) })}
            </span>
            <DocTranscript doc={d} />
          </div>
          {!readOnly && (d.uploaded_by_user_id === user?.id || isAdmin) && (
            <button type="button" className="btn-icon docs-delete-btn" title={t('docs.remove')} aria-label={t('docs.remove')}
              disabled={removal.deleting != null}
              onClick={() => removal.ask({ id: d.id, name: d.name, kind: d.kind, inComment: !!d.task_update_id, isRecording: d.is_recording })}>
              <Trash2 size={12} />
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <DocumentsAdd target={target} confirmTarget={confirmTarget} disabled={removal.deleting != null}
          onAdded={() => load().then(d => onChanged?.(d.length))} />
      )}
      {removal.prompt}
    </div>
  );
}
